import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import KanbanContext from '../../KanbanContext'
import { StatusKanbanSearchCondition } from '../../hooks/useSearchCondition'
import { DateTermVO } from '../../../../../domain/value-object/DateTermVO'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import { FontSize } from '../../../../../styles/commonStyles'
import SearchConditionSelect from './SearchConditionSelect'
import SearchConditionAutocomplete from './SearchConditionAutocomplete'
import { DateRangeItem } from '../Card/DateRangeItem'
import useAnchorButton from '../Filter/useAnchorButton'
import { Button } from '../../../../components/buttons'

type Props = {
  searchCondition: StatusKanbanSearchCondition
  onSearch: (condition?: StatusKanbanSearchCondition) => Promise<boolean>
  defaultOpen?: boolean
}

const SearchConditionPopper = ({
  searchCondition,
  onSearch,
  defaultOpen = false,
}: Props) => {
  const context = useContext(KanbanContext)
  const {
    isOpen: internalIsOpen,
    anchorEl,
    setAnchorEl,
    handleClose: internalHandleClose,
    toggleOpen: internalToggleOpen,
  } = useAnchorButton()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const [isChildEditing, setIsChildEditing] = useState(false)

  const isOpen = internalIsOpen

  // defaultOpenがtrueの場合、初回マウント時にPopperを開く
  const defaultOpenApplied = useRef(false)
  useEffect(() => {
    if (defaultOpen && !defaultOpenApplied.current && buttonRef.current) {
      setAnchorEl(buttonRef.current)
      defaultOpenApplied.current = true
    }
  }, [defaultOpen, setAnchorEl])

  // 開いた直後のClickAwayを無視するためのフラグ
  const justOpenedRef = useRef(false)
  useEffect(() => {
    if (isOpen) {
      justOpenedRef.current = true
      const timer = setTimeout(() => {
        justOpenedRef.current = false
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    internalHandleClose()
  }, [internalHandleClose])

  const toggleOpen = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      internalToggleOpen(event)
    },
    [internalToggleOpen]
  )

  // ローカル状態で編集中の検索条件を管理
  const [localCondition, setLocalCondition] =
    useState<StatusKanbanSearchCondition>(searchCondition)

  // Popperが開いた時に検索条件をローカル状態にコピー
  useEffect(() => {
    if (isOpen) {
      setLocalCondition(searchCondition)
    }
  }, [isOpen, searchCondition])

  const handleChildEditStart = useCallback(() => setIsChildEditing(true), [])
  const handleChildEditEnd = useCallback(() => setIsChildEditing(false), [])

  // ローカル状態の更新関数
  const handleLocalTeamChange = useCallback((uuid?: string) => {
    setLocalCondition(prev => ({
      ...prev,
      teamUuid: uuid,
      sprintUuid: undefined,
    }))
  }, [])

  const handleLocalSprintChange = useCallback((uuid?: string) => {
    setLocalCondition(prev => ({
      ...prev,
      sprintUuid: uuid,
    }))
  }, [])

  const handleLocalScheduledEndDateChange = useCallback((v?: DateTermVO) => {
    setLocalCondition(prev => ({
      ...prev,
      scheduledEndDate: v,
    }))
  }, [])

  // 検索ボタンクリック時
  const handleSearch = useCallback(async () => {
    const success = await onSearch(localCondition)
    if (success) {
      handleClose()
    }
  }, [localCondition, onSearch, handleClose])

  const filteredSprints = useMemo(() => {
    if (!localCondition.teamUuid) {
      return context.sprints
    }
    return context.sprints.filter(
      sprint => sprint.teamUuid === localCondition.teamUuid
    )
  }, [context.sprints, localCondition.teamUuid])

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (justOpenedRef.current) return
        isOpen && !isChildEditing && handleClose()
      }}
    >
      <Box>
        <Tooltip title={intl.formatMessage({ id: 'kanban.searchCondition' })}>
          <StyledIconButton ref={buttonRef} onClick={toggleOpen}>
            <SearchIcon sx={{ fontSize: '16px' }} />
          </StyledIconButton>
        </Tooltip>
        <Popper
          open={isOpen}
          anchorEl={anchorEl ?? buttonRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
        >
          <StyledPaper ref={paperRef}>
            <SearchConditionItem>
              <SearchConditionLabel>
                {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.team' })}
              </SearchConditionLabel>
              <SearchConditionSelect
                value={localCondition.teamUuid}
                options={context.teams}
                onChange={handleLocalTeamChange}
                onOpen={handleChildEditStart}
                onClose={handleChildEditEnd}
              />
            </SearchConditionItem>
            <SearchConditionItem>
              <SearchConditionLabel>
                {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.sprint' })}
              </SearchConditionLabel>
              <SearchConditionAutocomplete
                value={localCondition.sprintUuid}
                options={filteredSprints}
                onChange={handleLocalSprintChange}
                onOpen={handleChildEditStart}
                onClose={handleChildEditEnd}
                disabled={!localCondition.teamUuid}
                placeholder={
                  !localCondition.teamUuid
                    ? intl.formatMessage({
                        id: 'wbsItemSearchFilterPanel.selectTeam',
                      })
                    : undefined
                }
              />
            </SearchConditionItem>
            <SearchConditionItem>
              <SearchConditionLabel>
                {intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.scheduledEndDate',
                })}
              </SearchConditionLabel>
              <SearchConditionDateRange>
                <DateRangeItem
                  value={localCondition.scheduledEndDate}
                  onChange={handleLocalScheduledEndDateChange}
                  onOpen={handleChildEditStart}
                  onClose={handleChildEditEnd}
                />
              </SearchConditionDateRange>
            </SearchConditionItem>
            <SearchButtonWrapper>
              <Button onClick={handleSearch} colorPattern="skyBlue">
                {intl.formatMessage({ id: 'search' })}
              </Button>
            </SearchButtonWrapper>
          </StyledPaper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default SearchConditionPopper

const StyledIconButton = styled(IconButton)({
  background: colorPalette.monotone[1],
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  padding: '4px',
  color: colorPalette.monotone[4],
  '&:hover': { background: colorPalette.monotone[2] },
})

const StyledPaper = styled(Paper)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px',
  minWidth: '300px',
})

const SearchConditionItem = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
})

const SearchConditionLabel = styled(Typography)({
  color: colorPalette.monotone[5],
  fontSize: FontSize.MEDIUM,
})

const SearchConditionDateRange = styled('div')({
  padding: '4px 8px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
})

const SearchButtonWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '8px',
})
